import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useState,
} from 'react';
import { FormInstance, Schema, Text } from 'rsuite';
import SchemaTyped from 'rsuite/esm/Schema';
import PersonDetails from '../../PersonDetails';
import { FormModelPerson, Person } from 'src/types/person';
import { getInitialFormModelValues } from 'src/common/people';
import {
  companyNameRequiredValidation,
  companyNameValidation,
  emailValidation,
  nameRequiredValidation,
  phoneRequiredValidation,
  requiredValidation,
} from 'src/common/validation';

const { ObjectType } = SchemaTyped.Types;

const { BooleanType } = SchemaTyped.Types;

interface PropertyManagerFormProps {
  hideCompanyName?: boolean;
  initialValue?: FormModelPerson;
  isIndependent?: boolean;
  title: string;
}

export interface PropertyManagerFormRef {
  validateForm: () => boolean;
  getValues: () => Person;
}

export const PropertyManagerForm = forwardRef<
  PropertyManagerFormRef,
  PropertyManagerFormProps
>(({ hideCompanyName, initialValue, isIndependent, title }, ref) => {
  const personDetailsRef = useRef<FormInstance<FormModelPerson>>(null);
  const [value, setValue] = useState<FormModelPerson>(
    getInitialFormModelValues(initialValue)
  );
  useImperativeHandle(ref, () => ({
    validateForm: () => personDetailsRef.current?.check() ?? false,
    getValues: () => value,
  }));

  const propertyManagerValuesPerson = useMemo(
    () =>
      Schema.Model<FormModelPerson>({
        companyName: isIndependent
          ? companyNameValidation
          : companyNameRequiredValidation,
        completePhysicalAddress: ObjectType().isRequired(
          'Select a physical address from the dropdown to continue'
        ),
        email: emailValidation,
        firstName: nameRequiredValidation,
        lastName: nameRequiredValidation,
        mailingAddress: requiredValidation,
        phone: phoneRequiredValidation,
        sameAsPropertyAddress: BooleanType(),
      }),
    [isIndependent]
  );

  return (
    <div className="mt-8">
      <Text
        as="h5"
        className="text-bold"
      >
        {title}
      </Text>
      <div style={{ marginTop: '12px' }}>
        <PersonDetails
          formModel={propertyManagerValuesPerson}
          formValues={value}
          hiddenFields={{
            companyName: hideCompanyName,
            email: true,
            firstName: true,
            lastName: true,
            mailingAddress: true,
            phone: true,
          }}
          optionalFields={{
            companyName: isIndependent ? 'optional' : 'required',
          }}
          ref={personDetailsRef}
          setFormValues={setValue}
          validationModel={propertyManagerValuesPerson}
        />
      </div>
    </div>
  );
});

PropertyManagerForm.displayName = 'PropertyManagerForm';
